/**
 * Ovoz: brauzer `speechSynthesis` — faqat o'rganilayotgan tildagi ovoz. Rus ovozi bo'lmasa inglizcha o'qimaydi,
 * bir marta ko'rsatma (noVoice) ko'rsatadi. Mikrofon: Web Speech API `SpeechRecognition` (Telegram iOS'da yo'q → fallback).
 */
import { LANG, LN } from '../data';
import { getPlatform } from './telegram';

let voice: SpeechSynthesisVoice | null | undefined = null; // undefined = ovozlar hali yuklanmagan
export const canSpeak = () => { try { return 'speechSynthesis' in window; } catch { return false; } };

function pickVoice(): SpeechSynthesisVoice | null | undefined {
  const vs = speechSynthesis.getVoices();
  if (!vs.length) return undefined;
  const m = vs.filter((v) => v.lang.toLowerCase().replace('_', '-').startsWith(LANG));
  const score = (v: SpeechSynthesisVoice) => (v.lang.replace('_', '-') === LN.tts ? 4 : 0) + (/google|natural|neural|premium|enhanced|siri/i.test(v.name) ? 2 : 0) + (v.localService ? 0 : 1);
  return m.sort((a, b) => score(b) - score(a))[0] ?? null;
}

/** Ovoz topilmaganda chaqiriladi — UI bir marta ko'rsatma oynasini ochadi. */
export let onNoVoice: (() => void) | null = null;
export const setNoVoiceHandler = (f: () => void) => { onNoVoice = f; };
let warned = false;

function say(text: string, rate: number, tries = 0) {
  try {
    if (voice === null || voice === undefined) voice = pickVoice();
    if (voice === undefined) { if (tries < 10) { setTimeout(() => say(text, rate, tries + 1), 120); return; } voice = null; }
    if (!voice && LANG !== 'en') { if (!warned) { warned = true; onNoVoice?.(); } return; }
    const u = new SpeechSynthesisUtterance(text);
    u.lang = voice ? voice.lang : LN.tts; u.rate = rate; if (voice) u.voice = voice;
    speechSynthesis.cancel(); speechSynthesis.speak(u);
  } catch { /* ignore */ }
}
export const speak = (text: string) => say(text, 0.9);
export const speakSlow = (text: string) => say(text, 0.5);
/** Ovozni toʻxtatish (oʻyindan chiqqanda). */
export const hush = () => { try { speechSynthesis.cancel(); } catch { /* ignore */ } };

/**
 * Sonni oʻrganilayotgan tilda aytish (Sonlar oʻyini). `onEnd` — ovoz tugaganda yoki kutish muddati oʻtganda
 * (ovoz boʻlmasa ham) bir marta chaqiriladi: taymer ovoz tugashini kutmasdan ham boshlanadi.
 */
export function sayNumber(n: number, onEnd?: () => void) {
  let done = false;
  const fin = () => { if (done) return; done = true; onEnd?.(); };
  try {
    if (voice === null || voice === undefined) voice = pickVoice();
    const v = voice ?? null;
    if (!v && LANG !== 'en') { if (!warned) { warned = true; onNoVoice?.(); } }
    else {
      const u = new SpeechSynthesisUtterance(String(n));
      u.lang = v ? v.lang : LN.tts; u.rate = 0.95; if (v) u.voice = v;
      u.onend = fin; u.onerror = fin;
      speechSynthesis.cancel(); speechSynthesis.speak(u);
    }
  } catch { /* ignore */ }
  setTimeout(fin, 900 + String(n).length * 450);
}
try { speechSynthesis.onvoiceschanged = () => { voice = pickVoice(); }; speechSynthesis.getVoices(); } catch { /* ignore */ }

/* ---------- mikrofon ---------- */
export type SRCtor = new () => SpeechRecognitionLike;
export interface SpeechRecognitionLike {
  lang: string; maxAlternatives: number; interimResults: boolean;
  onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void; abort(): void;
}
export const SRClass = (): SRCtor | null => {
  try {
    // Telegram'ning o'rnatilgan WebView'lari (Android WebView, iOS/macOS WKWebView, Desktop) Web Speech API'ni qo'llamaydi:
    // konstruktor bo'lsa ham start() xato beradi yoki jim qoladi. Faqat haqiqiy brauzerda (web.telegram.org / Telegram'siz) yoqamiz.
    const pf = getPlatform();
    if (pf && !['web', 'weba', 'webk'].includes(pf)) return null;
    const w = window as unknown as { SpeechRecognition?: SRCtor; webkitSpeechRecognition?: SRCtor }; return w.SpeechRecognition || w.webkitSpeechRecognition || null;
  } catch { return null; }
};
/** null — sinalmagan, false — ishlamaydi, true — ishlaydi. */
export let SR_OK: boolean | null = null;
export const setSrOk = (v: boolean | null) => { SR_OK = v; };
